import React, { useState } from 'react';
import s from './ScheduleModal.module.css';
import Modal from '../../../../shared/ui/modal/Modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Spinner from '../../../../shared/ui/spinner/Spinner';
import Button from '../../../../shared/ui/button/Button';
import IconCheck from '../../../../shared/ui/icons/IconCheck';
import { updateProject } from '../../../../shared/api/textProjectApi';
import { useAppDispatch } from '../../../../store/store';

interface ScheduleModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedProjects: string[];
}

type ModalStep = 'date' | 'time' | 'confirmation' | 'loading' | 'success';

const ScheduleModal: React.FC<ScheduleModalProps> = ({ isOpen, onClose, selectedProjects }) => {
    const dispatch = useAppDispatch();
    const [currentStep, setCurrentStep] = useState<ModalStep>('date');
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState<string>('');

    const handleCancel = () => {
        if (currentStep === 'loading') {
            return;
        }

        if (currentStep === 'success') {
            handleSuccessClose();
            return;
        }

        setCurrentStep('date');
        setSelectedDate(null);
        setSelectedTime('');
        onClose();
    };

    const handleDateContinue = () => {
        if (selectedDate) {
            if (selectedTime) {
                const now = new Date();
                const isToday = selectedDate.toDateString() === now.toDateString();

                if (isToday) {
                    const [hours, minutes] = selectedTime.split(':');
                    const selectedTimeDate = new Date();
                    selectedTimeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

                    if (selectedTimeDate <= now) {
                        setSelectedTime('');
                    }
                }
            }
            setCurrentStep('time');
        }
    };

    const handleTimeContinue = () => {
        if (selectedTime) {
            setCurrentStep('confirmation');
        }
    };

    const handleBack = () => {
        if (currentStep === 'time') {
            setCurrentStep('date');
        } else if (currentStep === 'confirmation') {
            setCurrentStep('time');
        }
    };

    const handleConfirm = async () => {
        try {
            setCurrentStep('loading');

            if (selectedDate && selectedTime) {
                const [hours, minutes] = selectedTime.split(':');
                const scheduleDate = new Date(selectedDate);
                scheduleDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

                const isoString = scheduleDate.toISOString().replace('.000Z', '.000000Z');

                console.log('Scheduling with date:', isoString);

                const updatePromises = selectedProjects.map(async (projectId) => {
                    const updateData = {
                        id: projectId,
                        schedule: { timestamp: isoString },
                    };
                    console.log('Updating project:', updateData);
                    return dispatch(updateProject(updateData));
                });

                await Promise.all(updatePromises);
            }

            setCurrentStep('success');
        } catch (error) {
            console.error('Error scheduling posts:', error);
            setCurrentStep('date');
            setSelectedDate(null);
            setSelectedTime('');
            onClose();
        }
    };

    const handleSuccessClose = () => {
        setCurrentStep('date');
        setSelectedDate(null);
        setSelectedTime('');
        onClose();
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const renderDateSelection = () => (
        <div className={s.modal_content}>
            <h2 className={s.modal_title}>Schedule a date and time</h2>
            <div className={s.datepicker_container}>
                <DatePicker
                    selected={selectedDate}
                    onChange={(date: Date | null) => {
                        setSelectedDate(date);

                        if (date && selectedTime) {
                            const now = new Date();
                            const isToday = date.toDateString() === now.toDateString();

                            if (isToday) {
                                const [hours, minutes] = selectedTime.split(':');
                                const selectedTimeDate = new Date();
                                selectedTimeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

                                if (selectedTimeDate <= now) {
                                    setSelectedTime('');
                                }
                            }
                        }
                    }}
                    minDate={new Date()}
                    inline
                    calendarClassName={s.custom_datepicker}
                />
            </div>
            <div className={s.button_container}>
                <Button
                    onClick={handleCancel}
                    text="Cancel"
                    style={{ background: 'white', color: '#23262A', border: '1px solid #E5E7EB' }}
                />

                <Button
                    onClick={handleDateContinue}
                    text="Continue"
                    state={!selectedDate ? 'disabled' : 'enabled'}
                    style={{ background: '#FFDD35' }}
                />
            </div>
        </div>
    );

    const renderTimeSelection = () => {
        const timeSlots = [];
        const now = new Date();
        const isToday = selectedDate && selectedDate.toDateString() === now.toDateString();

        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                });

                if (isToday) {
                    const timeDate = new Date();
                    timeDate.setHours(hour, minute, 0, 0);
                    if (timeDate <= now) {
                        continue;
                    }
                }

                timeSlots.push({ value: time, display: displayTime });
            }
        }

        return (
            <div className={s.modal_content}>
                <h2 className={s.modal_title}>Schedule a date and time</h2>
                {/* <div className={s.time_notice}>
                    <p>Best time to post</p>
                    <p>Based on your engagement stats</p>
                </div> */}
                <div className={s.time_grid}>
                    {timeSlots.map((slot) => (
                        <button
                            key={slot.value}
                            className={`${s.time_slot} ${selectedTime === slot.value ? s.selected_time : ''}`}
                            onClick={() => setSelectedTime(slot.value)}
                        >
                            {slot.display}
                        </button>
                    ))}
                </div>
                <div className={s.button_container}>
                    <Button
                        onClick={handleCancel}
                        text="Cancel"
                        style={{
                            background: 'white',
                            color: '#23262A',
                            border: '1px solid #E5E7EB',
                        }}
                    />

                    <Button
                        onClick={handleTimeContinue}
                        text="Continue"
                        state={!selectedTime ? 'disabled' : 'enabled'}
                        style={{ background: '#FFDD35' }}
                    />
                </div>
            </div>
        );
    };

    const renderConfirmation = () => (
        <div className={s.modal_content}>
            <div className={s.clock_icon}>🕘</div>
            <h2 className={s.modal_title}>Confirm date and time</h2>
            <p className={s.confirmation_text}>Your post will be scheduled for:</p>
            <p className={s.confirmation_text}>
                {selectedDate && formatDate(selectedDate)},{' '}
                {selectedTime &&
                    new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                    })}
            </p>
            <div className={s.button_container}>
                <Button
                    onClick={handleCancel}
                    text="Cancel"
                    style={{ background: 'white', color: '#23262A', border: '1px solid #E5E7EB' }}
                />

                <Button
                    onClick={handleConfirm}
                    text="Confirm"
                    state={!selectedTime ? 'disabled' : 'enabled'}
                    style={{ background: '#FFDD35' }}
                />
            </div>
        </div>
    );

    const renderLoading = () => (
        <div className={s.modal_content}>
            <div className={s.loading_container}>
                <Spinner />
                <h2 className={s.modal_title}>Scheduling your posts...</h2>
                <p className={s.loading_text}>Please wait while we schedule your posts</p>
            </div>
        </div>
    );

    const renderSuccess = () => (
        <div className={s.modal_content}>
            <div className={s.success_icon}>
                <IconCheck />
            </div>
            <h2 className={s.modal_title}>Success! Post has been scheduled</h2>
            <p className={s.success_text}>
                You have successfully scheduled your post via SimpleCreate
            </p>
            <div className={s.button_container}>
                <Button
                    onClick={handleSuccessClose}
                    text="Got it!"
                    style={{ background: '#FFDD35' }}
                />
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 'date':
                return renderDateSelection();
            case 'time':
                return renderTimeSelection();
            case 'confirmation':
                return renderConfirmation();
            case 'loading':
                return renderLoading();
            case 'success':
                return renderSuccess();
            default:
                return renderDateSelection();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleCancel} width="450px">
            <div className={s.schedule_modal}>{renderCurrentStep()}</div>
        </Modal>
    );
};

export default ScheduleModal;
